import { useTranslation } from "@shared/utils/internationalization";
import Icon from "@ui-kit/icons/Icon.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { enums } from "@shared/constants";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import Box from "@ui-kit/layout/Box/Box.web";
import { ReactElement } from "react";
import { LowInventoryBadgeProps } from "./LowInventoryBadge.props";

const LowInventoryBadge = ({
  className,
  lowThreshold,
  middleThreshold,
  highThreshold,
  hideIcon = false,
  textLengthShort = false,
  level,
  isCalendarDay = false,
  isCalendarLegend = false,
  isTimeslotButton = false,
}: LowInventoryBadgeProps) => {
  const { t } = useTranslation("common");
  const [isSmallerThanXs] = useMediaQuery(`(max-width: 650px)`);
  const lowInventoryMap = enums.REMAINING_TICKETS_PERCENTAGE_MAP;

  // default badge element, displayed on non-calendar components
  const getDefaultElement = (
    threshold: number,
    color: string,
    icon: ReactElement
  ) => {
    const statusText = textLengthShort
      ? `${Math.round(threshold * 100)}% ${t("Sold")}`
      : `${t("Over")} ${Math.round(threshold * 100)}% ${t("Sold")}`;
    return (
      <Text
        as="span"
        display="flex"
        alignItems="center"
        color={isTimeslotButton ? "inherit" : color}
      >
        {!hideIcon && (
          <Box as="span" display="flex" alignItems="center" mr="2xs">
            {icon}
          </Box>
        )}
        {statusText}
      </Text>
    );
  };

  // badge element displayed on calendar days
  // on mobile screens, only the icon is shown
  const getCalendarDayElement = (threshold: number, icon: ReactElement) => {
    const statusText = `${Math.round(threshold * 100)}% ${t("Sold")}`;
    return isSmallerThanXs ? (
      icon
    ) : (
      <Text as="span" display="flex" alignItems="center" color="inherit">
        {statusText}
      </Text>
    );
  };

  // badge element displayed on the calendar legend
  // visible on mobile screens only
  const getCalendarLegendElement = (threshold: number, icon: ReactElement) => {
    const statusText = `${t("Selling Fast")} - ${t("Over")} ${Math.round(
      threshold * 100
    )}% ${t("Sold")}`;
    return (
      <Text
        display="flex"
        alignItems="center"
        fontSize="xs"
        color="primary.800"
      >
        {icon}
        {statusText}
      </Text>
    );
  };

  const getStatusMessage = (inputLevel: number) => {
    let statusElement = <></>;

    // 1st threshold
    if (inputLevel === lowInventoryMap.THRESHOLD_LOW.id) {
      // 1st threshold text that does not appear on the calendar
      if (!isCalendarDay && !isCalendarLegend) {
        statusElement = getDefaultElement(
          lowThreshold,
          "yellow.800",
          <Icon.Ticket.Outline color="yellow.800" boxSize="element.sm" />
        );
      }
      // 1st threshold text for the calendar (days only)
      else if (isCalendarDay) {
        statusElement = getCalendarDayElement(
          lowThreshold,
          <Icon.Ticket.Outline boxSize="element.xs" />
        );
      }
      // 1st threshold text for the calendar (legend only)
      else if (isCalendarLegend) {
        statusElement = getCalendarLegendElement(
          lowThreshold,
          <Icon.Ticket.Outline boxSize="element.md" marginRight="sm" />
        );
      }
    }
    // 2nd threshold
    else if (inputLevel === lowInventoryMap.THRESHOLD_MIDDLE.id) {
      // 2nd threshold text that does not appear on the calendar
      if (!isCalendarDay && !isCalendarLegend) {
        statusElement = getDefaultElement(
          middleThreshold,
          "yellow.800",
          <Icon.DangerCircle.Outline color="yellow.800" boxSize="element.sm" />
        );
      }
      // 2nd threshold text for the calendar (days only)
      else if (isCalendarDay) {
        statusElement = getCalendarDayElement(
          middleThreshold,
          <Icon.DangerCircle.Outline boxSize="element.xs" />
        );
      }
      // 2nd threshold text for the calendar (legend only)
      else if (isCalendarLegend) {
        statusElement = getCalendarLegendElement(
          middleThreshold,
          <Icon.DangerCircle.Outline boxSize="element.md" marginRight="sm" />
        );
      }
    }
    // 3rd threshold
    else if (inputLevel === lowInventoryMap.THRESHOLD_HIGH.id) {
      // 3rd threshold text that does not appear on the calendar
      if (!isCalendarDay && !isCalendarLegend) {
        statusElement = getDefaultElement(
          highThreshold,
          "red.600",
          <Icon.DangerTriangle.Outline color="red.600" boxSize="element.sm" />
        );
      }
      // 3rd threshold text for the calendar (days only)
      else if (isCalendarDay) {
        statusElement = getCalendarDayElement(
          highThreshold,
          <Icon.DangerTriangle.Outline boxSize="element.xs" />
        );
      }
      // 3rd threshold text for the calendar (legend only)
      else if (isCalendarLegend) {
        statusElement = getCalendarLegendElement(
          highThreshold,
          <Icon.DangerTriangle.Outline boxSize="element.md" marginRight="sm" />
        );
      }
    }

    return statusElement;
  };

  const isCompactContext = isTimeslotButton || isCalendarDay;
  let textFontSize: "10px" | "inherit" | "xs" = "xs";
  if (isCompactContext) {
    textFontSize = "10px";
  } else if (isCalendarLegend) {
    textFontSize = "inherit";
  }
  const textFontWeight =
    isCompactContext || isCalendarLegend ? "inherit" : "bold";

  return (
    <Text
      as="span"
      className={className}
      display="inline-block"
      fontSize={textFontSize}
      lineHeight="14px"
      fontWeight={textFontWeight}
    >
      {getStatusMessage(level)}
    </Text>
  );
};

export default LowInventoryBadge;
